import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, throwError } from 'rxjs';
import { AcademyAdministratorOperation } from '../../../../../model/administrator/AcademyAdministratorOperation';
import { AcademyAdministratorType } from '../../../../../model/administrator/AcademyAdministratorType';
import { AdministratorEntity } from '../../../../../model/administrator/AdministratorEntity';
import { Competition } from '../../../../../model/competitions/Competition';
import { HockeyCategory } from '../../../../../model/competitions/HockeyCategory';
import { CategoryLevel } from '../../../../../model/competitions/Level';
import { TeamRegistration } from '../../../../../model/competitions/TeamRegistration';
import { HttpResponse } from '../../../../../model/HttpResponse';
import { Member } from '../../../../../model/member';
import { QueryOptionDto } from '../../../../../model/QueryOptionDto';
import { Role } from '../../../../../model/Role';
import { CompetitionService } from '../../../../../services/competition.service';
import { GestionService } from '../../../../../services/gestion.service';
import { LoadingService } from '../../../../../services/loading.service';
import { UtilsService } from '../../../../../services/utils.service';

@Component({
  selector: 'app-hockey-team-members-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    TableModule,
    TagModule,
    ButtonModule,
    ToastModule,
    TooltipModule
  ],
  providers: [MessageService],
  templateUrl: './hockey-team-members-modal.component.html',
  styleUrl: './hockey-team-members-modal.component.scss',
})
export class HockeyTeamMembersModalComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() team!: TeamRegistration;
  @Input() category!: HockeyCategory;
  @Input() competition!: Competition;
  @Input() levels: CategoryLevel[] = [];

  @Output() onClose = new EventEmitter<void>();

  currentMembers: Member[] = [];
  activeTabIndex = 0;

  constructor(
    private competitionService: CompetitionService,
    private gestionService: GestionService,
    private loadingService: LoadingService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    // Component initialization - team members loaded in ngOnChanges
  }

  ngOnChanges(): void {
    if (this.visible && this.team) {
      this.loadTeamMembers();
      this.activeTabIndex = 0;
    }
  }

  private loadTeamMembers(): void {
    if (!this.team || !this.team.members) {
      this.currentMembers = [];
      return;
    }

    const memberIds = this.team.members.map(member => member.memberId);
    if (memberIds.length === 0) {
      this.currentMembers = [];
      return;
    }

    this.loadingService.showLoading();
    
    // Create conditionals for each member ID with OR connector
    const memberConditionals = memberIds.map((memberId, index) => ({
      conditionalConnector: 'OR',
      logicOperator: '=',
      evaluatedProperty: 'id',
      parameterName: `@MemberId${index}`,
      parameterValue: memberId,
    }));


    const options: QueryOptionDto = {
      options: {
        conditionals: [...memberConditionals],
      },
    };

    const entity: AdministratorEntity = {
      type: AcademyAdministratorType.MEMBERS,
      operation: AcademyAdministratorOperation.SELECT,
      payload: options,
    };

    this.gestionService
      .sendRequest(entity)
      .pipe(catchError(this.handleError.bind(this)))
      .subscribe((response: HttpResponse) => {
        if (response.code === 200 && response.response) {
          this.currentMembers = response.response as Member[];
        } else {
          this.currentMembers = [];
        }
        this.loadingService.hideLoading();
      });
  }


  onTabChange(event: any): void {
    this.activeTabIndex = event.index;
  }

  getMemberAge(birthDay: string): number {
    const today = new Date();
    const birth = new Date(birthDay);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }

  getGenderLabel(gender: string): string {
    return gender === 'M' ? 'Masculino' : gender === 'F' ? 'Femenino' : 'Otro';
  }

  getLevelName(levelId: string): string {
    const level = this.levels.find(l => l.id === levelId);
    return level ? level.name : 'Nivel no encontrado';
  }

  isPlayerRole(member: Member): boolean {
    return member.rol === Role.DEPORTISTA;
  }

  getPlayerNumber(member: Member): string | null {
    if (!this.isPlayerRole(member) || !this.team?.members) {
      return null;
    }

    const teamMember = this.team.members.find(tm => tm.memberId === member.id);
    if (teamMember?.customData?.playerNumber) {
      return teamMember.customData.playerNumber;
    }

    return null;
  }

  /**
   * Verifica si un miembro tiene error de categoría (inscrito con lógica antigua)
   */
  hasCategoryAgeError(member: Member): boolean {
    if (!this.isPlayerRole(member) || !this.category || !this.competition) {
      return false;
    }
    return UtilsService.hasCategoryAgeError(member.birthDay, this.category, this.competition);
  }

  onCloseModal(): void {
    this.activeTabIndex = 0;
    this.onClose.emit();
  }

  private handleError(error: HttpErrorResponse) {
    this.loadingService.hideLoading();
    if (error.status === 422) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin resultados',
        detail: 'No se encontraron resultados para la búsqueda realizada',
        life: 5000,
      });
    } else if (error.status === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'No se pudo establecer conexión con el servidor, por favor inténtalo nuevamente',
        life: 5000,
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
        life: 5000,
      });
    }
    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }
}